// ---- Model registry ----
// One uniform way to get any model's build function by id, whether it's a JSON
// document (content/models/*.json — parts / quadruped / humanoid, interpreted)
// or a TS-coded builder (coded.ts). Spec loaders (src/content/load.ts) bind each
// NPC/item/deco spec's `build` from its `model` ref through here, so buildNpc/
// buildItem/buildDeco and the studio catalog never know the difference.

import type { Prims } from "../meshkit.ts";
import { RAW } from "../content/manifest.ts";
import {
  interpretParts, resolvePalette, colorOf, ARCHETYPE_SLOTS, partsMinY,
  type ModelDoc, type Vec3, type Part,
} from "./parts.ts";
import type { PartGroupDoc } from "../content/schemas.ts";
import {
  resolveModelDoc, scalePrims, seededSigned, LANE_SHADE, LANE_SCALE,
} from "./variants.ts";
import { shade } from "../color.ts";
import { quadruped, quadAnchorPoints, type QuadrupedOptions } from "./quadruped.ts";
import { humanoid, type HumanoidOptions } from "./humanoid.ts";
import type { BeardStyle, BodyBuild, FaceExpression, HairStyle, Sex } from "../shared.ts";
import { bird, type BirdOptions } from "./bird.ts";
import { bug, type BugOptions } from "./bug.ts";
import { CODED_MODELS, type CodedBuilder } from "./coded.ts";
import { headwearHidesHair } from "./head-seat.ts";
import { scaleWearPart, wearScaleCtx, WEAR_SCALE_REGIONS } from "./wear-scale.ts";

export { headwearHidesHair } from "./head-seat.ts";

const docs = new Map<string, ModelDoc>();
for (const e of RAW.models) {
  const d = e.data as ModelDoc;
  docs.set(d.id, d);
}

const partGroups = new Map<string, PartGroupDoc>();
for (const e of RAW.parts) {
  const d = e.data as PartGroupDoc;
  partGroups.set(d.id, d);
}

/** Look up a parts-library doc (slot fillers — content/parts/*.json). */
export function partGroup(id: string): PartGroupDoc | undefined {
  return partGroups.get(id);
}

/** Every parts-library id — validation checks slot fillers against this. */
export function partGroupIds(): Set<string> {
  return new Set(partGroups.keys());
}

/** Every model id (JSON + coded) — validation checks model refs against this. */
export function modelIds(): Set<string> {
  return new Set([...docs.keys(), ...Object.keys(CODED_MODELS)]);
}

/** Look up a JSON model document (the studio tuning panel edits these live). */
export function modelDoc(id: string): ModelDoc | undefined {
  return docs.get(id);
}

/** Look up a model doc FLATTENED against its base (variants) — what actually
 *  builds. Non-variants return the raw doc. */
export function resolvedModelDoc(id: string): ModelDoc | undefined {
  const doc = docs.get(id);
  return doc && resolveModelDoc(doc, (b) => docs.get(b));
}

/** Build function for a parts-library doc standing ALONE on the ground — the
 *  drop/shop model of a wearable whose geometry lives in content/parts (its item
 *  doc has `wear.part` and no `model`). Slot-relative parts are authored around
 *  their anchor (a hat dips below y=0), so the whole group is lifted to rest
 *  feet-at-0 like every other item model. */
export function partGroundBuilder(id: string): CodedBuilder {
  return (p) => {
    const g = partGroups.get(id);
    if (!g) throw new Error(`unknown parts doc "${id}"`);
    const lift = -Math.min(0, partsMinY(g.parts as Part[]));
    interpretParts(p, g.parts as Part[], resolvePalette(g.palette ?? {}), { root: [0, lift, 0] });
  };
}

/** Resolve a model id to its build function. Throws on unknown id (validation
 *  catches dangling refs before this can fire at runtime). The seed param is
 *  the per-instance VARIATION seed (variantSeed(spawn id)) — only consumed
 *  when the doc carries a `variation` block. */
export function modelBuilder(id: string): CodedBuilder {
  const doc = docs.get(id);
  if (doc) return (p, seed) => buildFromDoc(p, resolveModelDoc(doc, (b) => docs.get(b)), seed);
  const coded = CODED_MODELS[id];
  if (coded) return coded;
  throw new Error(`unknown model "${id}"`);
}

/** Interpret a (resolved) model document: archetype body first (publishing
 *  anchors), the part list on top, then anchor-slot fillers (emitted inside
 *  their host limb, so headwear rides the head joint). Re-reads the doc every
 *  build, so studio edits to the live document rebuild faithfully.
 *  With a `variation` block + a seed, applies the deterministic per-instance
 *  jitter: a uniform palette lightness shift and a whole-build scale (via
 *  scalePrims — group.scale stays 1 for combat's flinch pop). */
export function buildFromDoc(p: Prims, doc: ModelDoc, seed?: number): void {
  const varn = doc.variation;
  if (varn?.scale && seed !== undefined) {
    p = scalePrims(p, 1 + seededSigned(seed, LANE_SCALE) * varn.scale);
  }
  const shadeJitter = varn?.shade && seed !== undefined
    ? seededSigned(seed, LANE_SHADE) * varn.shade
    : 0;
  const jitterPalette = (def: ModelDoc["palette"]) => {
    const resolved = resolvePalette(def ?? {});
    if (!shadeJitter) return resolved;
    for (const k of Object.keys(resolved)) resolved[k] = shade(resolved[k]!, shadeJitter);
    return resolved;
  };

  const palette = jitterPalette(doc.palette);
  let anchors: Record<string, Vec3> = { root: [0, 0, 0] };

  if (doc.archetype === "quadruped") {
    anchors = quadAnchorPoints(quadruped(p, hydrateQuadBody(doc.body ?? {}, palette)));
  } else if (doc.archetype === "humanoid") {
    // Face-slot role: face-replace fillers (goblin-face) suppress the default
    // humanoid eyes/smile so they don't stack under a full alternate face.
    // face-add (beards) leaves the default face on. Explicit body.head.features
    // still wins when set to false; replace forces false when unset/true-default.
    const bodyOpts = hydrateHumanoidBody(doc.body ?? {}, palette);
    const faceId = doc.slots?.face;
    if (faceId) {
      const filler = partGroups.get(faceId);
      if (filler?.role === "face-replace") {
        bodyOpts.head = { ...bodyOpts.head, features: false };
      }
    }
    // Headwear hair contract (mirrors player mergeWear): helmets/caps hide
    // hair by default; crowns set part `hidesHair: false` (gold-crown) so
    // hair stays. See headwearHidesHair().
    const headwearId = doc.slots?.headwear;
    if (headwearId && headwearHidesHair(partGroups.get(headwearId))) {
      bodyOpts.head = { ...bodyOpts.head, hairStyle: "none" };
    }
    anchors = humanoid(p, bodyOpts);
  } else if (doc.archetype === "bird") {
    anchors = bird(p, hydrateBirdBody(doc.body ?? {}, palette));
  } else if (doc.archetype === "bug") {
    anchors = bug(p, hydrateBugBody(doc.body ?? {}, palette));
  }

  interpretParts(p, doc.parts ?? [], palette, anchors);

  // Wear-slot fillers are AUTHORED against the average-male frame; on any
  // other body their radial dimensions scale by the formula factors so the
  // same doc clears a stout/female frame (null ctx = average male = the
  // fillers emit byte-identical — the snapshot contract). Only the paperdoll
  // body slots scale (WEAR_SCALE_REGIONS); doc.parts never do.
  const wearCtx = doc.archetype === "humanoid"
    ? wearScaleCtx(
        (doc.body as { sex?: Sex } | undefined)?.sex,
        (doc.body as { build?: BodyBuild } | undefined)?.build,
        (doc.body as { scale?: number } | undefined)?.scale,
      )
    : null;

  // Anchor-slot fillers (helmets/hats/packs). Socket slots (held/offhand) are
  // ITEMS, seated at the dressing layer (src/npc/dress.ts) — skipped here.
  const slotDefs = ARCHETYPE_SLOTS[doc.archetype];
  for (const [slot, fillerId] of Object.entries(doc.slots ?? {})) {
    const def = slotDefs[slot];
    if (!def || "socket" in def) continue;
    const filler = partGroups.get(fillerId);
    if (!filler) throw new Error(`model "${doc.id}": slot "${slot}" filler "${fillerId}" not in content/parts`);
    const at = anchors[def.anchor];
    if (!at) throw new Error(`model "${doc.id}": slot "${slot}" anchor "${def.anchor}" missing`);
    const fillerPalette = jitterPalette(filler.palette);
    // Slot seat is `root` so default `at` stays slot-relative (unchanged). Full
    // host anchors are also passed so wear fillers may set part.anchor to
    // helpers (shoulderShelfL/R, collar, belt — see WEAR_FILLER_ANCHORS).
    const fillerAnchors: Record<string, [number, number, number]> = {
      ...anchors,
      root: at,
    };
    const region = wearCtx ? WEAR_SCALE_REGIONS[slot] : undefined;
    const fillerParts = region
      ? filler.parts.map((pt) => scaleWearPart(pt, region, wearCtx!))
      : filler.parts;
    const emitFiller = () =>
      interpretParts(p, fillerParts, fillerPalette, fillerAnchors);
    if (def.limb) p.inLimb(def.limb, emitFiller);
    else emitFiller();
  }
}

// ---------------------------------------------------------------------------
// Body hydration — JSON body params carry palette KEYS for colors; the archetype
// functions take plain numbers.
// ---------------------------------------------------------------------------

function col(v: unknown, palette: Record<string, number>): number | undefined {
  return typeof v === "string" ? colorOf(v, palette) : undefined;
}

export function hydrateQuadBody(
  body: Record<string, unknown>,
  palette: Record<string, number>,
): QuadrupedOptions {
  const num = (k: string) => (typeof body[k] === "number" ? (body[k] as number) : undefined);
  return {
    bodyColor: col(body.bodyColor, palette) ?? 0x888888,
    legColor: col(body.legColor, palette),
    headColor: col(body.headColor, palette),
    hoofColor: col(body.hoofColor, palette),
    length: num("length"),
    width: num("width"),
    thickness: num("thickness"),
    legHeight: num("legHeight"),
    legRadius: num("legRadius"),
    headSize: num("headSize"),
    neck: num("neck"),
    neckRaise: num("neckRaise"),
    neckJoint: body.neckJoint === true,
    tailJoint: body.tailJoint === true,
  };
}

export function hydrateBirdBody(
  body: Record<string, unknown>,
  palette: Record<string, number>,
): BirdOptions {
  const num = (k: string) => (typeof body[k] === "number" ? (body[k] as number) : undefined);
  return {
    bodyColor: col(body.bodyColor, palette) ?? 0x888888,
    headColor: col(body.headColor, palette),
    wingColor: col(body.wingColor, palette),
    tailColor: col(body.tailColor, palette),
    legColor: col(body.legColor, palette),
    beakColor: col(body.beakColor, palette),
    length: num("length"),
    width: num("width"),
    thickness: num("thickness"),
    legHeight: num("legHeight"),
    headSize: num("headSize"),
    neck: num("neck"),
    tailLift: num("tailLift"),
  };
}

export function hydrateBugBody(
  body: Record<string, unknown>,
  palette: Record<string, number>,
): BugOptions {
  const num = (k: string) => (typeof body[k] === "number" ? (body[k] as number) : undefined);
  return {
    bodyColor: col(body.bodyColor, palette) ?? 0x888888,
    headColor: col(body.headColor, palette),
    legColor: col(body.legColor, palette),
    abdomenColor: col(body.abdomenColor, palette),
    legPairs: num("legPairs"),
    length: num("length"),
    width: num("width"),
    thickness: num("thickness"),
    stance: num("stance"),
    legSpread: num("legSpread"),
    headSize: num("headSize"),
    abdomen: num("abdomen"),
  };
}

export function hydrateHumanoidBody(
  body: Record<string, unknown>,
  palette: Record<string, number>,
): HumanoidOptions {
  const lower = (body.lower ?? { kind: "legs", color: "#666666" }) as Record<string, unknown>;
  const torso = (body.torso ?? {}) as Record<string, unknown>;
  const arms = body.arms as Record<string, unknown> | undefined;
  const head = body.head as Record<string, unknown> | undefined;
  return {
    skin: col(body.skin, palette) ?? 0xe0ac69,
    scale: typeof body.scale === "number" ? body.scale : undefined,
    // Frame + silhouette (schema-validated enums — see shared.ts SEXES/BODY_BUILDS).
    sex: body.sex as Sex | undefined,
    build: body.build as BodyBuild | undefined,
    lower:
      lower.kind === "skirt"
        ? { kind: "skirt", color: col(lower.color, palette) ?? 0x666666, hem: col(lower.hem, palette) }
        : { kind: "legs", color: col(lower.color, palette) ?? 0x666666, boot: col(lower.boot, palette), short: lower.short === true },
    torso: {
      color: col(torso.color, palette) ?? 0x777777,
      width: typeof torso.width === "number" ? torso.width : undefined,
      height: typeof torso.height === "number" ? torso.height : undefined,
      depth: typeof torso.depth === "number" ? torso.depth : undefined,
    },
    arms: arms
      ? {
          sleeve: col(arms.sleeve, palette),
          length: typeof arms.length === "number" ? arms.length : undefined,
        }
      : undefined,
    head: head
      ? {
          hair: col(head.hair, palette),
          hairStyle: head.hairStyle as HairStyle | undefined,
          beard: col(head.beard, palette),
          beardStyle: head.beardStyle as BeardStyle | undefined,
          eye: col(head.eye, palette),
          features: head.features === false ? false : undefined,
          expression: head.expression as FaceExpression | undefined,
        }
      : undefined,
  };
}
